import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../components/Breadcrumbs';
import './Terms.css';

function Terms() {
    return (
        <>
            <Helmet>
                <title>Terms of Service - NeuAIs GPU Cloud Platform Agreement</title>
                <meta name="description" content="NeuAIs Terms of Service: Usage policies, acceptable use, billing terms, and legal agreements for our GPU cloud computing platform." />
                <meta name="keywords" content="terms of service, user agreement, GPU terms, cloud computing terms, acceptable use policy" />
                <link rel="canonical" href="https://lab.neuais.com/terms" />
            </Helmet>

            <Breadcrumbs />

            <div className="info-page">
                <div className="info-container">
                    <h1>Terms of Service</h1>
                    <div className="info-content">
                        <section>
                            <p><em>Last updated: December 13, 2024</em></p>
                            <p>
                                By using NeuAIs, you agree to these Terms of Service. Please read them carefully.
                            </p>
                        </section>

                        <section>
                            <h2>Service Description</h2>
                            <p>
                                NeuAIs provides cloud-based GPU computing resources with Jupyter notebook environments.
                                Our service includes:
                            </p>
                            <ul>
                                <li>On-demand GPU access (NVIDIA A100/H100)</li>
                                <li>Containerized execution environments</li>
                                <li>Persistent storage and data management</li>
                                <li>Collaboration and sharing features</li>
                                <li>API access for automation</li>
                            </ul>
                            <p>
                                We reserve the right to modify, update, or discontinue features with 30 days' notice.
                            </p>
                        </section>

                        <section>
                            <h2>Account Responsibilities</h2>
                            <ul>
                                <li><strong>Account Security:</strong> Maintain confidentiality of credentials</li>
                                <li><strong>Accurate Information:</strong> Provide truthful registration details</li>
                                <li><strong>One Account Per User:</strong> Sharing accounts is prohibited</li>
                                <li><strong>Age Requirement:</strong> Must be 18+ or have parental consent</li>
                                <li><strong>Notification:</strong> Report security breaches immediately</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Acceptable Use Policy</h2>
                            <h3>Permitted Activities</h3>
                            <ul>
                                <li>Machine learning research and development</li>
                                <li>Data science and analytics workloads</li>
                                <li>Academic research and education</li>
                                <li>Commercial AI model training</li>
                            </ul>

                            <h3>Prohibited Activities</h3>
                            <ul>
                                <li>❌ Cryptocurrency mining (any blockchain)</li>
                                <li>❌ Distributed denial-of-service (DDoS) attacks</li>
                                <li>❌ Illegal content generation or distribution</li>
                                <li>❌ Circumventing security or quota limits</li>
                                <li>❌ Unauthorized access to other users' data</li>
                                <li>❌ Reselling GPU time without authorization</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Resource Usage & Billing</h2>
                            <ul>
                                <li><strong>GPU Hours:</strong> Billed per minute of active usage</li>
                                <li><strong>Storage:</strong> First 50GB free, $0.10/GB/month thereafter</li>
                                <li><strong>Fair Use:</strong> Excessive usage may trigger rate limiting</li>
                                <li><strong>Overage Charges:</strong> Automatically charged to payment method</li>
                                <li><strong>Refunds:</strong> Pro-rated for service outages exceeding SLA</li>
                            </ul>
                            <p>
                                See full pricing at <a href="https://neuais.com/pricing">neuais.com/pricing</a>
                            </p>
                        </section>

                        <section>
                            <h2>Data Ownership & IP</h2>
                            <ul>
                                <li><strong>Your Content:</strong> You retain all rights to your code and data</li>
                                <li><strong>Our Platform:</strong> NeuAIs retains all IP for the platform itself</li>
                                <li><strong>License Grant:</strong> You grant us license to host/process your content</li>
                                <li><strong>Backup Responsibility:</strong> Maintain external backups of critical data</li>
                            </ul>
                            <p>
                                We do not claim ownership of models, datasets, or code you create.
                            </p>
                        </section>

                        <section>
                            <h2>Service Level Agreement (SLA)</h2>
                            <ul>
                                <li><strong>Uptime Guarantee:</strong> 99.9% monthly uptime (excludes maintenance)</li>
                                <li><strong>Scheduled Maintenance:</strong> Announced 7 days in advance</li>
                                <li><strong>SLA Credits:</strong> 10% credit for each 0.1% below 99.9%</li>
                                <li><strong>Incident Response:</strong> P0 issues addressed within 15 minutes</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Termination</h2>
                            <p>We may suspend or terminate accounts for:</p>
                            <ul>
                                <li>Violation of these Terms or Acceptable Use Policy</li>
                                <li>Payment failure or chargebacks</li>
                                <li>Illegal activities or security threats</li>
                                <li>Prolonged inactivity (180+ days with notice)</li>
                            </ul>
                            <p>
                                You may cancel anytime. Prepaid credits are non-refundable but remain valid for 12 months.
                            </p>
                        </section>

                        <section>
                            <h2>Limitation of Liability</h2>
                            <p>
                                NeuAIs is provided "as is" without warranties of any kind. We are not liable for:
                            </p>
                            <ul>
                                <li>Data loss due to user error or force majeure</li>
                                <li>Service interruptions beyond reasonable control</li>
                                <li>Consequential or indirect damages</li>
                                <li>Third-party content or integrations</li>
                            </ul>
                            <p>
                                Maximum liability is limited to fees paid in the preceding 12 months.
                            </p>
                        </section>

                        <section>
                            <h2>Dispute Resolution</h2>
                            <ul>
                                <li><strong>Governing Law:</strong> Delaware, United States</li>
                                <li><strong>Arbitration:</strong> Binding arbitration for disputes exceeding $10,000</li>
                                <li><strong>Class Action Waiver:</strong> Individual disputes only</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Changes to Terms</h2>
                            <p>
                                We may update these Terms with 30 days' notice via email. Continued use
                                constitutes acceptance of updated Terms.
                            </p>
                        </section>

                        <section>
                            <h2>Contact Information</h2>
                            <p>
                                Questions about these Terms? <Link to="/contact">Contact us</Link> or email
                                <a href="mailto:legal@neuais.com"> legal@neuais.com</a>.
                            </p>
                            <p>
                                Review our <Link to="/privacy">Privacy Policy</Link> for data handling details.
                            </p>
                        </section>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Terms;
